import React, { useContext, useEffect } from "react";
import {
    ButtonProps,
    ButtonRenderProps,
    Checkbox,
    ClearFiltersButton,
    ContentSection,
    createListTableColumns,
    Grid,
    GridColumn,
    GridRow,
    ListTable,
    renderBooleanIcon,
    SearchBox,
    Stack
} from "sprint-common-web";

import { LanguageResponse } from "../../../common/api/admin-api/GetLanguageListResponse";
import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { adminRoutes } from "../../AdminRoutes";
import { LanguageListingContext } from "../context/LanguageListingContext";
import { AddLanguageButton } from "../create/AddLanguageButton";
import { useLanguagesListingData } from "./hooks/useLanguagesListingData";
import { useLanguagesListingFilters } from "./hooks/useLanguagesListingFilters";
import { renderCodes } from "./utils/renderCodes";

export interface LanguageItem {
    id: string;
    name: string;
    code: string;
    iso639: string;
    laRg?: string;
    isDubbing: boolean;
    isSubtitle: boolean;
    isCaption: boolean;
    isModified?: boolean;
    buzzCodes: {
        audio: string[];
        dynamicSubtitles: string[];
        embeddedSubtitles: string[];
    };
    shreqCodes: {
        audio: string[];
        dynamicSubtitles: string[];
        embeddedSubtitles: string[];
    };
}

export const mapToLanguageItem = (response: LanguageResponse): LanguageItem => ({
    id: response.id,
    name: response.name,
    code: response.code,
    iso639: response.iso639,
    laRg: response.laRg,
    isDubbing: response.isDubbing,
    isSubtitle: response.isSubtitle,
    isCaption: response.isCaption,
    buzzCodes: {
        audio: response.buzzAudioCodes ?? [],
        dynamicSubtitles: response.buzzDynamicSubtitleCodes ?? [],
        embeddedSubtitles: response.buzzEmbeddedSubtitleCodes ?? []
    },
    shreqCodes: {
        audio: response.shreqAudioCodes ?? [],
        dynamicSubtitles: response.shreqDynamicSubtitleCodes ?? [],
        embeddedSubtitles: response.shreqEmbeddedSubtitleCodes ?? []
    }
});

export const LanguagesListing: React.FC = () => {
    const {
        filters,
        setSort,
        setPage,
        setPageSize,
        setName,
        setCode,
        toggleDubbingsOnly,
        toggleSubtitlesOnly,
        clearFilters
    } = useLanguagesListingFilters();

    const { items, totalItems, saveData } = useLanguagesListingData();
    const { languageItems, isLoading } = useContext(LanguageListingContext);

    useEffect(() => {
        if (languageItems.length) {
            saveData(languageItems, {
                name: filters.name,
                page: filters.page,
                pageSize: filters.pageSize,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
                code: filters.code,
                dubbingsOnly: filters.dubbingsOnly,
                subtitlesOnly: filters.subtitlesOnly
            });
        }
    }, [filters, languageItems]);

    const columns = createListTableColumns<LanguageItem>([
        {
            type: "basic",
            key: "name",
            name: "Name",
            minWidth: 100,
            maxWidth: 210,
            isSortable: true
        },
        {
            type: "basic",
            key: "code",
            name: "Code",
            minWidth: 80,
            maxWidth: 100,
            isSortable: true
        },
        {
            type: "basic",
            key: "iso639",
            name: "Iso639",
            minWidth: 80,
            maxWidth: 100,
            isSortable: true
        },
        {
            type: "basic",
            key: "laRg",
            name: "LaRg",
            minWidth: 80,
            maxWidth: 100,
            isSortable: true
        },
        {
            type: "basic",
            key: "isDubbing",
            name: "Is Dubbing",
            minWidth: 70,
            maxWidth: 70,
            isSortable: true,
            onRender: renderBooleanIcon
        },
        {
            type: "basic",
            key: "isSubtitle",
            name: "Is Subtitle",
            minWidth: 70,
            maxWidth: 70,
            isSortable: true,
            onRender: renderBooleanIcon
        },
        {
            type: "basic",
            key: "isCaption",
            name: "Is Caption",
            minWidth: 70,
            maxWidth: 70,
            isSortable: true,
            onRender: renderBooleanIcon
        },
        {
            type: "basic",
            key: "buzzCodes",
            name: "Buzz codes",
            minWidth: 350,
            isResizable: true,
            onRender: (item) =>
                renderCodes({
                    audioCodes: item?.buzzCodes.audio,
                    dynamicSubtitles: item?.buzzCodes.dynamicSubtitles,
                    embeddedSubtitles: item?.buzzCodes.embeddedSubtitles
                })
        },
        {
            type: "basic",
            key: "shreqCodes",
            name: "Shreq codes",
            minWidth: 350,
            isResizable: true,
            onRender: (item) =>
                renderCodes({
                    audioCodes: item?.shreqCodes.audio,
                    dynamicSubtitles: item?.shreqCodes.dynamicSubtitles,
                    embeddedSubtitles: item?.shreqCodes.embeddedSubtitles
                })
        }
    ]);

    const addLanguageButtonProps: ButtonRenderProps<ButtonProps> = {
        render: AddLanguageButton
    };

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={adminRoutes} />
            </PageHeader>
            <ContentSection header={"Languages"} buttons={[addLanguageButtonProps]}>
                <Grid>
                    <GridRow>
                        <GridColumn sm={2}>
                            <SearchBox
                                placeholder="Name"
                                onChange={setName}
                                value={filters.name}
                                testId="languages-filters-name"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <SearchBox
                                placeholder="Code"
                                onChange={setCode}
                                value={filters.code}
                                testId="languages-filters-code"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <Checkbox
                                label="Dubbings Only"
                                onChange={toggleDubbingsOnly}
                                checked={filters.dubbingsOnly}
                                testId="languages-filters-dubbings-only"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <Checkbox
                                label="Subtitles Only"
                                onChange={toggleSubtitlesOnly}
                                checked={filters.subtitlesOnly}
                                testId="languages-filters-subtitles-only"
                            />
                        </GridColumn>
                        <GridColumn sm={4}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton isLabeled={true} onClear={clearFilters} />
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                items={items}
                                pagination={{
                                    page: filters.page,
                                    pageSize: filters.pageSize,
                                    totalItems: totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                isLoading={isLoading}
                                layoutMode="fixedColumns"
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};
